import { useState, useEffect } from "react";
import PageHeader from "./boxes/PageHeader";
import MainContent from "./boxes/MainContent";
import PageBody from "./boxes/PageBody";
import Tour from "reactour";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import tourSettings from "../components/tours/tourSettings";
import { useTranslation } from "react-i18next";
import { useAuth } from "./utils/oidc/AuthProvider";
import { envVariables } from "../config/appConfig";
import { auth as oidcAuth } from "../services/oidc/authConfig";

function getNotificationFlagFromDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("settingsDB", 1);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("flags")) {
        db.createObjectStore("flags");
      }
    };

    request.onsuccess = (event) => {
      try {
        const db = event.target.result;
        const tx = db.transaction("flags", "readonly");
        const store = tx.objectStore("flags");
        const getRequest = store.get("notificationsEnabled");
        getRequest.onsuccess = () => resolve(getRequest.result ?? true);
        getRequest.onerror = reject;
      } catch (err) {
        reject(err);
      }
    };

    request.onerror = reject;
  });
}

const SettingsPage = ({ props }) => {
  const { t, i18n } = useTranslation(["settings", "common"]);
  const [isTourOpen, setTourOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem("notificationsEnabled") !== "false";
  });

  const openTour = () => setTourOpen(true);
  const closeTour = () => setTourOpen(false);
  const disableBody = (target) => disableBodyScroll(target);
  const enableBody = (target) => enableBodyScroll(target);

  const handleToggleNotifications = (enabled) => {
    setNotificationsEnabled(enabled);
    localStorage.setItem("notificationsEnabled", enabled);
    navigator.serviceWorker.controller?.postMessage({
      type: "TOGGLE_NOTIFICATIONS",
      enabled,
    });
  };

  useEffect(() => {
    const init = async () => {
      const flag = await getNotificationFlagFromDB();
      if (notificationsEnabled !== flag) {
        setNotificationsEnabled(flag);
      }

      openTour();

      const stored = localStorage.getItem("isTourOpen");
      if (stored === null) {
        localStorage.setItem("isTourOpen", true);
        setTourOpen(false);
      } else {
        setTourOpen(JSON.parse(stored));
      }

      localStorage.setItem("notificationsEnabled", notificationsEnabled);

      const getActiveServiceWorker = async () => {
        const registration = await navigator.serviceWorker.ready;
        while (!registration.active) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return registration.active;
      };

      const sendMessageToServiceWorker = async (message) => {
        try {
          const sw = await getActiveServiceWorker();
          sw.postMessage(message);
        } catch (err) {
          console.warn("Failed to message SW:", err);
        }
      };

      await sendMessageToServiceWorker({
        type: "TOGGLE_NOTIFICATIONS",
        enabled: notificationsEnabled,
      });
    };

    init();
  }, [notificationsEnabled]);

  return (
    <MainContent>
      <PageHeader title={t("pageTitle")} />
      <PageBody>
        <div className="dashboard-card">
          <ul>
            <li className="dashboard-list-item">
              <span>
                {t("notificationSliderLabel")}: &nbsp;&nbsp;
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={notificationsEnabled}
                    onChange={(e) =>
                      handleToggleNotifications(e.target.checked)
                    }
                  />
                  <span className="slider round" />
                </label>
              </span>
            </li>
            <li className="dashboard-list-item">
              {envVariables.REACT_APP_FEATURE_ENABLE_KEYCLOAK_SSO ? (
                <p
                  onClick={() =>
                    oidcAuth.initiateLogin({ kc_action: "UPDATE_PASSWORD" })
                  }
                  className="e2e-change-password-link"
                >
                  {t("changePasswordLinkText")}
                </p>
              ) : (
                <a href="/change-password" className="e2e-change-password-link">
                  {t("changePasswordLinkText")}
                </a>
              )}
            </li>
            {/* 
            <li className="dashboard-list-item">
              <a href={`${getAccountsDashboardUrl()}/welcome?src=enrollee`}>
                {t('settings.manage_accounts')}
              </a>
            </li> 
            */}
            <li className="dashboard-list-item">
              {envVariables.REACT_APP_FEATURE_ENABLE_KEYCLOAK_SSO ? (
                <p
                  className="e2e-logout-link"
                  onClick={() =>
                    oidcAuth.logout(window.location.origin + "/login")
                  }
                >
                  {t("logoutLinkText")}
                </p>
              ) : (
                <a href="/logout" className="e2e-logout-link">
                  {t("logoutLinkText")}
                </a>
              )}
            </li>
          </ul>
        </div>
      </PageBody>
      <Tour
        onRequestClose={closeTour}
        steps={tourSettings(t, i18n)}
        isOpen={isTourOpen}
        maskClassName="mask"
        className="helper"
        rounded={12}
        onAfterOpen={disableBody}
        onBeforeClose={enableBody}
      />
    </MainContent>
  );
};

export default SettingsPage;
